import React, { useEffect, useState } from 'react';
import { getStatusOptions, getOwnershipOptions } from '../utils/statusOptions';
import { CONDITION_OPTIONS } from '../utils/filterEngine';
import '../styles/Modal.css';

// Media types whose sub-model actually has an ownership_status/condition column
// (mirrors the list in QuickEditDetails.js - keep both in sync).
const OWNERSHIP_TYPES = ['Manga', 'Book', 'PhysicalGame', 'Music'];

const PRIVACY_OPTIONS = [
  { value: '0', label: 'Public' },
  { value: '1', label: 'Logged in users only' },
  { value: '2', label: 'Allowed users only' },
  { value: '3', label: 'Only visible to me' },
  { value: '4', label: 'Hidden' },
];

// Status the modal defaults to regardless of whatever the backend actually stored - always
// assume nothing has been consumed yet (some add flows set an unrelated/stale value here).
const UNCONSUMED_STATUS = {
  Anime: 'Plan to Watch',
  AnimeMovie: 'Plan to Watch',
  AnimeSerie: 'Plan to Watch',
  ScreenMedia: 'Plan to Watch',
  Manga: 'Plan to Read',
  Book: 'Plan to Read',
  SteamGame: 'Unplayed',
  PhysicalGame: 'Unplayed',
  Music: 'Not Listened',
};

const EMPTY_FIELDS = { status: '', ownership: '', condition: '', privacy: '0', score: '', note: '' };

// Lightweight optional-fields modal shown right after adding a media item, so ownership/status/
// privacy/score/personal note can be set immediately without opening the full edit page.
// Ownership/privacy/note are pre-filled from the item's real just-created values (GET /media/<id>);
// status always starts at an explicit "unconsumed" default and score always starts blank (see
// UNCONSUMED_STATUS above). Save only sends fields actually changed from those defaults -
// skipping is always a single click away.
const QuickSettingsModal = ({ isOpen, onClose, mediaId, mediaType, apiBaseUrl, title }) => {
  const [initial, setInitial] = useState(EMPTY_FIELDS);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !mediaId) return;
    setLoading(true);
    fetch(`${apiBaseUrl}/media/${mediaId}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        const loaded = {
          status: UNCONSUMED_STATUS[mediaType] || '',
          ownership: data.ownership_status || 'Not Owned',
          condition: '',
          privacy: data.private !== undefined && data.private !== null ? String(data.private) : '0',
          score: '',
          note: data.private_note || '',
        };
        setInitial(loaded);
        setFields(loaded);
      })
      .catch((err) => console.error('Failed to load media defaults:', err))
      .finally(() => setLoading(false));
  }, [isOpen, mediaId, mediaType, apiBaseUrl]);

  if (!isOpen) return null;

  const showOwnership = OWNERSHIP_TYPES.includes(mediaType);
  const setField = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const saveField = (field, value) =>
    fetch(`${apiBaseUrl}/update_quick_setting/${mediaId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ field, value }),
    });

  const handleSave = async () => {
    const updates = [];
    if (fields.status !== initial.status && fields.status) updates.push(saveField('status', fields.status));
    if (showOwnership && fields.ownership !== initial.ownership) updates.push(saveField('ownership_status', fields.ownership));
    if (showOwnership && fields.condition !== initial.condition && fields.condition) updates.push(saveField('condition', fields.condition));
    if (fields.privacy !== initial.privacy) updates.push(saveField('private', fields.privacy));
    if (fields.score !== initial.score) updates.push(saveField('score', fields.score));
    if (fields.note !== initial.note) updates.push(saveField('private_note', fields.note));

    if (updates.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await Promise.all(updates);
    } catch (err) {
      console.error('Failed to save quick settings:', err);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content quick-settings-content" onClick={(e) => e.stopPropagation()}>
        <h2>Added{title ? `: ${title}` : ''}</h2>
        <p>Set a few details now, or skip and edit them later.</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="quick-settings-row">
              <label>Status:</label>
              <select value={fields.status} onChange={(e) => setField('status', e.target.value)}>
                {!fields.status && <option value="">Not set</option>}
                {getStatusOptions(mediaType).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {showOwnership && (
              <div className="quick-settings-row">
                <label>Ownership:</label>
                <select value={fields.ownership} onChange={(e) => setField('ownership', e.target.value)}>
                  {getOwnershipOptions(mediaType).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            )}

            {showOwnership && (
              <div className="quick-settings-row">
                <label>Condition:</label>
                <select value={fields.condition} onChange={(e) => setField('condition', e.target.value)}>
                  <option value="">Not set</option>
                  {CONDITION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="quick-settings-row">
              <label>Privacy:</label>
              <select value={fields.privacy} onChange={(e) => setField('privacy', e.target.value)}>
                {PRIVACY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="quick-settings-row">
              <label>Score:</label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                placeholder="Not set"
                value={fields.score}
                onChange={(e) => setField('score', e.target.value)}
              />
            </div>

            <div className="quick-settings-row">
              <label>Note:</label>
              <textarea
                rows={3}
                placeholder="Personal note (optional)"
                value={fields.note}
                onChange={(e) => setField('note', e.target.value)}
              />
            </div>
          </>
        )}

        <div className="modal-buttons">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Skip</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickSettingsModal;
